import React, { useEffect, useMemo, useRef, useState } from 'react';

interface MobileGlassSliderProps {
  items: React.ReactNode[];
  height?: number; // content height inside the card
  autoAdvanceMs?: number; // e.g. 5000
  showDots?: boolean;
}

// A lightweight, mobile-first, glassmorphism slider
// - One slide visible at a time
// - Touch swipe navigation
// - Optional auto-advance
// - Dots indicator
const MobileGlassSlider: React.FC<MobileGlassSliderProps> = ({
  items,
  height = 180,
  autoAdvanceMs = 6000,
  showDots = true,
}) => {
  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const canSlide = items.length > 1;
  const minSwipeDistance = 40;

  const goTo = (i: number) => {
    if (!canSlide || isTransitioning || i === index) return;
    setIsTransitioning(true);
    setIndex(i);
    setTimeout(() => setIsTransitioning(false), 280);
  };

  const next = () => goTo((index + 1) % items.length);
  const prev = () => goTo((index - 1 + items.length) % items.length);

  // Auto-advance
  useEffect(() => {
    if (!canSlide || autoAdvanceMs <= 0) return;
    const id = setInterval(next, autoAdvanceMs);
    return () => clearInterval(id);
  }, [index, items.length, autoAdvanceMs, canSlide]);

  // Touch handlers
  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const onTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null) return;
    const dist = touchStartX.current - touchEndX.current;
    if (dist > minSwipeDistance) next();
    if (dist < -minSwipeDistance) prev();
  };

  const slides = useMemo(() => items.map((node, i) => {
    const active = i === index;
    return (
      <div
        key={i}
        style={{
          position: 'absolute',
          inset: 0,
          padding: '0 12px',
          opacity: active ? 1 : 0,
          transform: `translateX(${active ? '0' : (i > index ? '100%' : '-100%')}) scale(${active ? 1.0 : 0.98})`,
          transition: 'opacity 0.28s ease, transform 0.28s ease',
          pointerEvents: active ? 'auto' : 'none',
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            flex: 1,
            borderRadius: 14,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.78), rgba(255,255,255,0.62))',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border: '1px solid rgba(255,255,255,0.55)',
            boxShadow: '0 10px 28px rgba(34, 34, 69,0.12), inset 0 1px 0 rgba(77, 182, 159,0.8)',
            padding: 12,
            overflow: 'hidden',
            height,
          }}
        >
          {node}
        </div>
      </div>
    );
  }), [items, index, height]);

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 16,
        background: 'linear-gradient(120deg, rgba(20,20,35,0.85), rgba(60,70,120,0.75))',
        border: '1px solid rgba(255,255,255,0.18)',
        boxShadow: '0 16px 40px rgba(40, 60, 120, 0.35), 0 2px 10px rgba(0,0,0,0.12)',
        padding: '12px 0 6px',
      }}
    >
      {/* Slides */}
      <div
        style={{ position: 'relative', height: height + 0, overflow: 'hidden' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {slides}
      </div>

      {/* Controls */}
      {canSlide && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 10px 2px' }}>
          {showDots && items.map((_, i) => (
            <button
              key={`dot-${i}`}
              onClick={() => goTo(i)}
              disabled={isTransitioning}
              aria-label={`Go to slide ${i + 1}`}
              style={{
                width: 8,
                height: 8,
                minWidth: 8,
                minHeight: 8,
                borderRadius: 999,
                margin: '0 5px',
                border: '1px solid rgba(255,255,255,0.55)',
                background: i === index ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)',
                boxShadow: i === index ? '0 0 0 4px rgba(255,255,255,0.15)' : 'none',
                transition: 'all 0.2s ease',
                padding: 0,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileGlassSlider;
